import {action, computed, makeObservable, observable} from 'mobx'
import {DebouncedFuncLeading, throttle} from 'lodash'
import {OcWebsocket, SnackHandlerInstance} from '@oc/shell-core'
import {LastDataChangeService, Ticker} from '@oc/utils'
import {DeploymentStatus, type DeploymentStatusInfo} from '@oc/deployment'
import {messages} from '../../translations'

interface RkLastDataChangeUpdate {
    accountId: string
    readykitId: string
}

export class RkLastDataChangeState {
    @observable public isConnected = false
    @observable public dialogOpen = false
    @observable public lastDeploymentTimestamp?: number

    @observable private lastDataChange?: ReadonlyMap<string, number>

    private accountId?: string
    private readykitId?: string
    private lastDataChangeWebsocket?: OcWebsocket<RkLastDataChangeUpdate>
    private deploymentWebsocket?: OcWebsocket<DeploymentStatusInfo>
    private loadDataThrottled: DebouncedFuncLeading<() => Promise<void>>
    private refreshTicker: Ticker

    private lastDataChangeService = new LastDataChangeService()

    constructor() {
        makeObservable(this)

        this.loadDataThrottled = throttle(this.loadData, 30_000, {leading: true, trailing: true})
        this.refreshTicker = new Ticker(5 * 60 * 1000, this.loadDataThrottled)
    }

    /**
     * Returns list of tuples, each tuple being [resource name changed after last deployment, timestamp of resource change]
     */
    @computed
    public get changedResources(): [string, number][] {
        const deploymentTimestamp = this.lastDeploymentTimestamp
        return deploymentTimestamp && this.lastDataChange
            ? Array.from(this.lastDataChange.entries()).filter(([_, x]) => x > deploymentTimestamp)
            : []
    }

    @computed
    public get hasChangesAfterDeployment(): boolean {
        return this.changedResources.length > 0
    }

    @action.bound
    public async connect(accountId: string, readykitId: string) {
        this.accountId = accountId
        this.readykitId = readykitId

        await Promise.all([
            this.connectToLastDataChangeUpdates(this.accountId),
            this.connectToDeploymentUpdates(this.accountId),
            this.loadData()
        ])

        this.isConnected = true
    }

    @action.bound
    public async connectCypress(accountId: string, readykitId: string) {
        this.accountId = accountId
        this.readykitId = readykitId

        this.refreshTicker.start()
        await this.loadData()

        this.isConnected = true
    }

    @action.bound
    private async connectToLastDataChangeUpdates(accountId: string) {
        try {
            this.lastDataChangeWebsocket = new OcWebsocket('last-data-change', accountId, this.onLastDataChangeUpdate)
            await this.lastDataChangeWebsocket.connect()
        } catch (err) {
            console.warn('Failed to subscribe to last-data-change updates, falling back to periodic update')

            this.lastDataChangeWebsocket?.disconnect()
            this.lastDataChangeWebsocket = undefined
            this.refreshTicker.start()
        }
    }

    @action.bound
    public onLastDataChangeUpdate(data: RkLastDataChangeUpdate) {
        if (data.accountId === this.accountId && data.readykitId === this.readykitId) {
            this.loadDataThrottled()
        }
    }

    @action.bound
    private async connectToDeploymentUpdates(accountId: string) {
        try {
            this.deploymentWebsocket = new OcWebsocket('deploy-status-change', accountId, this.onDeploymentFinished)
            await this.deploymentWebsocket.connect()
        } catch (err) {
            console.warn('Failed to subscribe to deployment updates, falling back to periodic update')

            this.deploymentWebsocket?.disconnect()
            this.deploymentWebsocket = undefined
            this.refreshTicker.start()
        }
    }

    @action.bound
    public onDeploymentFinished(data: DeploymentStatusInfo) {
        if (
            data.account === this.accountId &&
            data.rkUuid === this.readykitId &&
            data.last.status === DeploymentStatus.succeeded
        ) {
            this.loadDataThrottled()
        }
    }

    @action.bound
    public disconnect() {
        this.isConnected = false
        this.accountId = undefined
        this.readykitId = undefined

        this.lastDataChangeWebsocket?.disconnect()
        this.lastDataChangeWebsocket = undefined

        this.deploymentWebsocket?.disconnect()
        this.deploymentWebsocket = undefined

        this.loadDataThrottled.cancel()
        this.refreshTicker.stop()
    }

    @action.bound
    private async loadData() {
        if (!this.accountId || !this.readykitId) {
            return
        }

        try {
            const response = await this.lastDataChangeService.getLastDataChangeForRK(this.accountId, this.readykitId)

            this.lastDataChange = response.lastDataChange
            this.lastDeploymentTimestamp = response.lastDeploymentTimestamp
        } catch (err) {
            SnackHandlerInstance.logError(messages.getText('errors', 'changesAfterLastDeploy', err))
            this.lastDataChange = undefined
            this.lastDeploymentTimestamp = undefined
        }
    }

    @action.bound
    public openDialog() {
        this.dialogOpen = true
    }

    @action.bound
    public closeDialog() {
        this.dialogOpen = false
    }
}
